import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useMe } from '@/auth/useAuth';
import { usePortalLocale } from '@/lib/i18n/PortalLocaleContext';
import { parentStudioHref } from '@/auth/parentStudio';
import { CourseMarkdown } from '@/components/CourseMarkdown';
import { PortalSectionTitle } from './PortalSectionTitle';
import { ParentClassDetails } from './ParentClassDetails';
import {
  parentCourseError,
  type ParentCourse,
  type ParentCourseSummary,
} from './parentCourseLearning';

export function ParentCoursesPage({ kind }: { kind?: 'class' | 'self-paced' }) {
  const { t } = usePortalLocale();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const me = useMe('user');
  const parentReady = me.data?.kind === 'user' && me.data.role === 'parent' && !!me.data.family_id;
  const ownerKey = me.data?.kind === 'user' ? me.data.sub : null;
  const familyKey = me.data?.kind === 'user' ? me.data.family_id : null;
  const courses = useQuery({
    queryKey: ['parent-courses', ownerKey, familyKey],
    queryFn: () => api<ParentCourseSummary[]>('/parent-courses', { principal: 'user' }),
    enabled: parentReady && !kind,
  });
  const course = useQuery({
    queryKey: ['parent-courses', ownerKey, familyKey, kind, id],
    queryFn: () =>
      api<ParentCourse>(
        `/parent-courses/${kind === 'class' ? 'classes' : 'self-paced'}/${encodeURIComponent(id)}`,
        { principal: 'user' },
      ),
    enabled: parentReady && !!kind && !!id,
    // Studio creation/completion can change this view while the route is unmounted.
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const studioKind = ['game', 'blocks', 'code'].find((value) =>
    course.data?.allowed_kinds?.includes(value),
  );
  const courseHref = `/portal/learning/${kind === 'class' ? 'classes' : 'self-paced'}/${encodeURIComponent(id)}`;
  const studioPath = (projectId: string, projectKind = studioKind) =>
    parentStudioHref(
      `/learn/${projectKind === 'blocks' ? 'blocks' : projectKind === 'code' ? 'code' : 'playground'}/${projectId}?course=${encodeURIComponent(courseHref)}`,
    );
  const start = useMutation({
    mutationFn: (mission: { id: string; title: string }) =>
      api<{ id: string }>('/projects', {
        principal: 'user',
        method: 'POST',
        body: {
          title: mission.title,
          kind: studioKind,
          product_line: 'line_b_coding',
          mission_id: mission.id,
        },
      }),
    onSuccess: (project) => navigate(studioPath(project.id)),
    onError: () => {
      void course.refetch();
    },
  });
  const query = kind ? course : courses;
  const nextLesson = course.data?.lessons.find(
    (lesson) => lesson.state !== 'locked' && lesson.state !== 'done',
  );
  const finished = course.data?.lessons.filter((lesson) => lesson.state === 'done').length ?? 0;
  return (
    <section className="card-base" data-testid="parent-course-learning">
      <PortalSectionTitle
        title={kind ? (course.data?.title ?? t('Your course')) : t('Parent courses')}
        icon="learning"
        tone="mint"
      />
      <p className="lead-text mt-4">
        {t(
          'Your family purchase includes your own course access. Your work and progress belong to you. AI help uses your family’s Stars.',
        )}
      </p>
      <div className="mt-4 flex flex-wrap gap-3">
        {kind && (
          <Link to="/portal/learning" className="btn-pill-secondary">
            {t('All your courses')}
          </Link>
        )}
        <Link to="/portal/projects" className="btn-pill-ghost">
          {t('Your saved projects')}
        </Link>
        <Link to="/portal/courses#upcoming" className="btn-pill-ghost">
          {t('Family bookings')}
        </Link>
      </div>
      {(me.isError || (me.isSuccess && !parentReady)) && (
        <div role="alert" className="mt-4 rounded-2xl bg-wash-sunshine p-4">
          <p>{t('Sign in to a parent account with a family to open your courses.')}</p>
          <Link to="/portal/login" className="btn-pill-secondary mt-3">
            {t('Parent sign-in')}
          </Link>
        </div>
      )}
      {(me.isLoading || (parentReady && query.isLoading)) && (
        <p className="mt-4 text-slate2" role="status">
          {t('Loading your courses…')}
        </p>
      )}
      {query.isError && (
        <div role="alert" className="mt-4 rounded-2xl bg-wash-sunshine p-4">
          <p>{t(parentCourseError(query.error))}</p>
          <div className="mt-3 flex flex-wrap gap-3">
            <button
              className="btn-pill-secondary"
              disabled={query.isFetching}
              onClick={() => void query.refetch()}
            >
              {query.isFetching ? t('Trying again…') : t('Try again')}
            </button>
            <Link className="btn-pill-ghost" to="/portal/courses#upcoming">
              {t('Check family booking')}
            </Link>
          </div>
        </div>
      )}
      {!kind && parentReady && courses.isSuccess && courses.data.length === 0 && (
        <div className="mt-5 rounded-2xl bg-wash-mint p-5">
          <h2 className="text-lg font-bold text-ink">{t('No courses are available yet')}</h2>
          <p className="mt-2 text-ink-soft">
            {t(
              'Purchased courses appear here after booking and payment are confirmed. You can use Creative Code Studio without booking a course.',
            )}
          </p>
          <Link to="/portal/courses#catalogue" className="btn-pill-primary mt-4">
            {t('Browse available classes')}
          </Link>
          <Link to={parentStudioHref('/learn/playground/new')} className="btn-pill-secondary mt-4">
            {t('Open Creative Code Studio')}
          </Link>
        </div>
      )}
      {!kind && parentReady && courses.isSuccess && (
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {courses.data.map((row) => (
            <Link
              key={row.key}
              to={`/portal/learning/${row.kind === 'class' ? 'classes' : 'self-paced'}/${encodeURIComponent(row.id)}`}
              className="rounded-2xl border border-hairline bg-canvas-pure p-5 transition hover:border-brand-mint focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-mint"
            >
              <p className="text-sm text-slate2">
                {row.kind === 'class' ? t('Family class') : t('Learn at your own pace')}
              </p>
              <h2 className="mt-2 text-lg font-bold text-ink">{row.title}</h2>
              <span className="mt-3 inline-block font-bold text-ink-soft">{t('Enter as yourself →')}</span>
            </Link>
          ))}
        </div>
      )}
      {kind && parentReady && course.isSuccess && (
        <div className="mt-5 space-y-5">
          {kind === 'class' && familyKey && (
            <ParentClassDetails classId={id} familyId={familyKey} />
          )}
          <section className="rounded-2xl bg-wash-mint p-5" aria-label={t('Learning together')}>
            <h2 className="text-lg font-bold text-ink">{t('Learning together')}</h2>
            <p className="mt-2 text-ink-soft">
              {t(
                'On one device, take turns discussing and testing the work. The signed-in person owns the project. Save and leave before changing accounts.',
              )}
            </p>
            <p className="mt-2 text-ink-soft">
              {t(
                'On separate devices, open the same lesson in each person’s account. You each keep your own work and progress.',
              )}
            </p>
            <p className="mt-2 text-sm font-semibold text-ink">
              {t('You are learning as the parent. Work started here is saved to your account.')}
            </p>
          </section>
          {course.data.description && <CourseMarkdown text={course.data.description} />}
          {course.data.lessons.length === 0 ? (
            <p className="text-slate2">
              {t(
                'Lessons have not been added yet. Your course access is recorded. Check with your teacher before the class.',
              )}
            </p>
          ) : (
            <section
              className="rounded-2xl border border-brand-mint bg-canvas-pure p-5"
              aria-label={t('Your next step')}
            >
              <p className="font-semibold text-ink">
                {t('{done} of {total} lessons completed in your account', {
                  done: finished,
                  total: course.data.lessons.length,
                })}
              </p>
              <p className="mt-2 text-ink-soft">
                {nextLesson
                  ? t(
                      'Next in your progress: {title}. During class, follow the lesson your teacher names.',
                      { title: nextLesson.title },
                    )
                  : finished === course.data.lessons.length
                    ? t('You have completed these lessons. Reopen your finished work below.')
                    : t('Finish the previous lesson to unlock the next one.')}
              </p>
              {nextLesson && (
                <a className="btn-pill-primary mt-3" href={`#lesson-${nextLesson.id}`}>
                  {t('Go to my next lesson')}
                </a>
              )}
            </section>
          )}
          {course.data.lessons.map((lesson) => (
            <article
              id={`lesson-${lesson.id}`}
              key={lesson.id}
              className="scroll-mt-6 rounded-2xl border border-hairline bg-canvas-pure p-5"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-bold text-ink">{lesson.title}</h2>
                <span className="text-sm font-semibold text-slate2">
                  {lesson.state === 'done'
                    ? t('Completed by you')
                    : lesson.state === 'locked'
                      ? t('Locked')
                      : t('Ready to learn')}
                </span>
              </div>
              {lesson.focus && <p className="mt-2 text-ink-soft">{lesson.focus}</p>}
              {lesson.deliverable && (
                <p className="mt-2 text-slate2">{t('What you make: {item}', { item: lesson.deliverable })}</p>
              )}
              {lesson.state === 'locked' ? (
                <p className="mt-3 font-semibold text-slate2">{t('Finish the previous lesson first.')}</p>
              ) : (
                <div className="mt-4 space-y-4">
                  {lesson.missions.map((mission) => (
                    <section
                      key={mission.id}
                      className="rounded-2xl bg-surface p-4"
                      aria-label={t('Task: {title}', { title: mission.title })}
                    >
                      <h3 className="font-bold text-ink">{mission.title}</h3>
                      {mission.content_md && (
                        <div className="mt-3">
                          <CourseMarkdown text={mission.content_md} />
                        </div>
                      )}
                      {mission.project ? (
                        <Link
                          to={studioPath(mission.project.id, mission.project.kind)}
                          className="btn-pill-primary mt-4"
                        >
                          {mission.project.status === 'accepted'
                            ? t('Open your finished work: {title}', { title: mission.title })
                            : t('Continue your work: {title}', { title: mission.title })}
                        </Link>
                      ) : studioKind ? (
                        <button
                          type="button"
                          disabled={start.isPending}
                          onClick={() => start.mutate(mission)}
                          className="btn-pill-primary mt-4 disabled:opacity-50"
                        >
                          {start.isPending && start.variables?.id === mission.id
                            ? t('Opening…: {title}', { title: mission.title })
                            : t('Start: {title}', { title: mission.title })}
                        </button>
                      ) : (
                        <p className="mt-3 font-semibold text-ink-soft">
                          {t(
                            'Do this activity with your child using the instructions above. This activity does not use a coding workspace.',
                          )}
                        </p>
                      )}
                      {start.isError && start.variables?.id === mission.id && (
                        <div role="alert" className="mt-3 rounded-2xl bg-wash-sunshine p-4">
                          <p>{t(parentCourseError(start.error, 'start'))}</p>
                          <button
                            className="btn-pill-secondary mt-3"
                            onClick={() => {
                              start.reset();
                              void course.refetch();
                            }}
                          >
                            {t('Refresh saved work')}
                          </button>
                        </div>
                      )}
                    </section>
                  ))}
                </div>
              )}
            </article>
          ))}
          <p className="text-sm text-ink-soft">
            {t(
              'When you leave the workspace, check its save status. Use its Home or Back control to return to this course. Next time, choose Parent courses on your home page.',
            )}
          </p>
        </div>
      )}
    </section>
  );
}
